<!-- Responsibility: Render the AI/settings content surface hosted inside the generic modal.
Scope: Settings content and save behavior only; modality is owned by modal. -->
<settings-dialog>
  <article class="settings-dialog">
    <header class="sm-header">
      <h2>AI CONFIGURATION</h2>
      <button class="close-btn modal-close-hit" aria-label="Close AI settings" onclick={ closeNow }>X</button>
    </header>

    <nav class="sm-tabs" aria-label="AI Configuration Tabs">
      <button class={ state.tab === 'basic' ? 'active' : '' } onclick={ () => switchTab('basic') }>Managed AI (Recommended)</button>
      <button class={ state.tab === 'advanced' ? 'active' : '' } onclick={ () => switchTab('advanced') }>Custom Keys (Advanced)</button>
    </nav>

    <section class="sm-body">
      <article if={ state.tab === 'basic' } class="sm-tab-panel">
        <div class="basic-setup-card">
          <h3>Cloud Infrastructure</h3>
          <p>AnyQuest runs on dynamic AI generation. You are currently using our managed cloud infrastructure.</p>
          <ul>
            <li><strong>Zero setup required</strong></li>
            <li><strong>High-speed inference</strong></li>
            <li><strong>Securely routed via Vertex AI</strong></li>
          </ul>
          <p class="coming-soon-note">Managed onboarding is currently paused for beta testers. Use the <strong>Custom Keys</strong> tab below whenever you need your own Ollama/Gemini credentials or want to preview future access.</p>
        </div>
      </article>

      <article if={ state.tab === 'advanced' } class="sm-tab-panel" ref="form">
        <section class="settings-stack">
          <div class="settings-panel">
            <div class="panel-header">
              <h3>Unified model map</h3>
              <p>Enable providers, choose their model, and assign the same catalog to each job bucket in one place.</p>
            </div>

            <div class="model-group">
              <div class="model-group-header">
                <h4>Provider models</h4>
                <p>Set one remembered model per provider. This is the provider-level default the runtime can inherit from.</p>
              </div>
              <div each={ a in state.viewModel.providerAssignments } class="provider-card" data-target-id={ a.id }>
                <div class="pc-header">
                  <span class="provider-status-dot { state.status[a.providerId] ? 'online' : 'offline' }"></span>
                  <strong class="pc-label">{ a.targetLabel }</strong>
                  <span class={ state.status[a.providerId] ? 'pc-badge online' : 'pc-badge offline' }>{ a.targetHint }</span>
                </div>
                <div class="pc-desc">{ a.desc }</div>
                <label if={ a.canToggle } class="pc-checkbox-label">
                  <input type="checkbox" checked={ a.enabled } onchange={ (e) => updateProviderEnabled(a.providerId, e) }> Enable { a.label }
                </label>
                <div if={ a.baseUrlField } class="form-group">
                  <label>BASE URL</label>
                  <input type="text" name={ a.baseUrlField } value={ state.config[a.baseUrlField] } oninput={ updateField } autocomplete="off">
                </div>
                <div if={ a.hardwareField } class="form-group ollama-hardware-group">
                  <label>YOUR HARDWARE <span class="pc-hint">guides model recommendations</span></label>
                  <select name="ollamaHardware" value={ state.config.ollamaHardware } onchange={ onHardwareChange }>
                    <option value="cpu">CPU only - best with 3-7B models</option>
                    <option value="4gb">4 GB VRAM - up to 7B models</option>
                    <option value="8gb">8 GB VRAM - up to 13B models</option>
                    <option value="16gb">16 GB+ VRAM - 30B+ models</option>
                  </select>
                </div>
                <div if={ a.keyField } class="form-group">
                  <label>{ a.label } KEY</label>
                  <input type="password" name={ a.keyField } value={ state.config[a.keyField] } oninput={ updateField } autocomplete="off" placeholder="API KEY" />
                </div>
                <div if={ a.proxyField } class="form-group">
                  <label>PROXY URL</label>
                  <input type="text" name={ a.proxyField } value={ state.config[a.proxyField] } oninput={ updateField } autocomplete="off" placeholder="https://example.com/proxy" />
                </div>
                <div class="form-group">
                  <label>MODEL</label>
                  <select value={ a.selectedRef } onchange={ (e) => updateAssignmentModel(a, e) }>
                    <option each={ m in a.models } value={ m.ref } disabled={ !m.available }>{ m.label } · { m.score }/100 · { m.costLabel }{ m.providerId === 'ollama' ? ' · ' + m.hardwareLabel : '' }</option>
                  </select>
                </div>
                <div class="provider-default-note">{ a.selectedModel || 'Auto' }</div>
              </div>
            </div>

            <div class="model-group">
              <div class="model-group-header">
                <h4>Job allocations</h4>
                <p>Assign the model catalog to each job bucket. These choices drive the runtime router.</p>
              </div>
              <div each={ a in state.viewModel.jobAssignments } class="job-section-card" data-target-id={ a.id }>
                <div class="pc-header">
                  <span class="provider-status-dot { state.status[decodeModelRef(a.selectedRef)?.providerId] ? 'online' : 'offline' }"></span>
                  <strong class="pc-label">{ a.targetLabel }</strong>
                  <span class={ state.status[decodeModelRef(a.selectedRef)?.providerId] ? 'pc-badge online' : 'pc-badge offline' }>{ a.targetHint }</span>
                </div>

                <div class="pc-desc">{ a.desc }</div>
                <div class="form-group">
                  <label>MODEL</label>
                  <select value={ a.selectedRef } onchange={ (e) => updateAssignmentModel(a, e) }>
                    <option each={ m in a.models } value={ m.ref } disabled={ !m.available }>{ m.label } · { m.score }/100 · { m.costLabel }{ m.providerId === 'ollama' ? ' · ' + m.hardwareLabel : '' }</option>
                  </select>
                </div>
                <div class="provider-default-note">{ a.selectedModel || 'Auto' }</div>
                <div class="recommendation-strip">
                  <div each={ m in a.models.slice(0, 4) } class="recommendation-pill { m.selected ? 'selected' : '' }">
                    <span>{ m.providerLabel }</span>
                    <strong>{ m.model }</strong>
                    <small>{ m.score }/100 · { m.costLabel }{ m.providerId === 'ollama' ? ' · ' + m.hardwareLabel : '' }</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <p class="sm-privacy-note">Your API keys are stored in your browser only and sent directly to the AI provider. This server never sees them.</p>
      </article>
    </section>

    <footer class="sm-footer actions">
      <button class="action-btn primary" onclick={ save }>SAVE &amp; APPLY</button>
      <div class="sm-status">{ state.saveStatus }</div>
    </footer>
  </article>

  <script>
    import { dispatcher } from '../../../app-states.js'
    import { TOPICS } from "@anyquest/shared-core"
    import { loadAIConfig, saveAIConfig } from '../../../ai/config.js'
    import { router as aiRouter } from '../../../ai/router.js'
    import {
      buildAISettingsViewModel,
      decodeModelRef,
      loadAIModelRoutingState,
      normalizeAIModelRoutingState,
      saveAIModelRoutingState,
    } from '../../../ai/model-settings.js'

    export default {
      state: {
        tab: 'advanced',
        config: {},
        routing: {
          assignments: {},
        },
        status: {},
        viewModel: {
          assignments: [],
        },
        ollamaModels: [],
        ollamaLoading: false,
        saveStatus: '',
        openToken: 0,
      },

      onMounted() {
        this.props.registerModalApi?.({ beforeModalClose: () => this.beforeModalClose() })
        this.syncForOpen(this.props)
      },

      onBeforeUpdate(props) {
        const nextToken = Number(props?.openToken ?? 0)
        if (nextToken !== Number(this.state.openToken ?? 0)) this.syncForOpen(props)
      },

      syncForOpen(props) {
        const config = loadAIConfig()
        const routing = loadAIModelRoutingState()
        this.update({
          openToken: Number(props?.openToken ?? 0),
          tab: props?.content?.initialTab === 'basic' ? 'basic' : 'advanced',
          config,
          routing,
          saveStatus: '',
        })
        this.refreshStatus()
        this.refreshViewModel()
        if (config.ollamaEnabled) this.loadOllamaModels()
      },

      beforeModalClose() {
        this.saveSilently()
        return true
      },

      async closeNow() {
        await this.props.requestModalClose?.('close-button')
      },

      async refreshStatus() {
        try {
          const status = await aiRouter.getStatus()
          this.update({ status })
        } catch {}
      },

      refreshViewModel() {
        this.update({
          viewModel: buildAISettingsViewModel(
            this.state.config,
            this.state.routing,
            this.state.ollamaModels,
          ),
        })
      },

      switchTab(tab) {
        this.update({ tab })
      },

      updateField(e) {
        const { name, value, type, checked } = e.target
        const val = type === 'checkbox' ? checked : value
        const config = { ...this.state.config, [name]: val }
        this.update({ config })
        this.refreshViewModel()
      },

      onHardwareChange(e) {
        this.updateField(e)
        this.refreshViewModel()
      },

      async loadOllamaModels() {
        this.update({ ollamaLoading: true })
        try {
          const { OllamaProvider } = await import('../../../ai/providers/ollama.js')
          const provider = new OllamaProvider(this.state.config.ollamaBaseUrl)
          const names = await provider.listModels()
          this.update({ ollamaModels: names, ollamaLoading: false })
          this.refreshViewModel()
        } catch {
          this.update({ ollamaModels: [], ollamaLoading: false })
          this.refreshViewModel()
        }
      },

      updateProviderEnabled(providerId, e) {
        const { checked } = e.target
        this.update({
          config: {
            ...this.state.config,
            [`${providerId}Enabled`]: checked,
          },
        })
        this.refreshViewModel()
        this.refreshStatus()
      },

      updateAssignmentModel(target, e) {
        const { value } = e.target
        const next = decodeModelRef(value)
        const routing = {
          ...this.state.routing,
          assignments: {
            ...this.state.routing.assignments,
            [target.id]: value,
          },
        }
        const config = { ...this.state.config }
        if (target.kind === 'provider' && target.providerId === 'ollama' && next?.model) {
          config.ollamaModel = next.model
          if (!config.ollamaFastModel) config.ollamaFastModel = next.model
          if (!config.ollamaQualityModel) config.ollamaQualityModel = next.model
        }
        if (target.kind === 'job' && next?.providerId === 'ollama' && next.model) {
          config.ollamaModel = next.model
          if (target.slot === 'coding') config.ollamaFastModel = next.model
          if (target.slot === 'quality' || target.slot === 'prose') config.ollamaQualityModel = next.model
        }
        this.update({ routing, config })
        this.refreshViewModel()
      },

      normalizeConfig() {
        const cfg = { ...this.state.config }
        if (cfg.openaiEnabled && !cfg.openaiApiKey) cfg.openaiEnabled = false
        if (cfg.anthropicEnabled && !cfg.anthropicApiKey) cfg.anthropicEnabled = false
        if (cfg.groqEnabled && !cfg.groqApiKey) cfg.groqEnabled = false
        if (cfg.geminiEnabled && !cfg.geminiApiKey && !cfg.geminiProxyUrl) cfg.geminiEnabled = false
        return cfg
      },

      saveSilently() {
        const cfg = this.normalizeConfig()
        const routing = normalizeAIModelRoutingState(this.state.routing, cfg)
        saveAIConfig(cfg)
        saveAIModelRoutingState(routing)
        dispatcher.trigger(this, TOPICS.ui.aiConfigSaved, cfg)
      },

      save() {
        this.update({ saveStatus: 'Saving...' })
        const cfg = this.normalizeConfig()
        const routing = normalizeAIModelRoutingState(this.state.routing, cfg)
        this.update({ config: cfg, routing })
        saveAIConfig(cfg)
        saveAIModelRoutingState(routing)
        dispatcher.trigger(this, TOPICS.ui.aiConfigSaved, cfg)
        aiRouter.getStatus().then((status) => {
          this.update({ status, saveStatus: 'Config saved & applied.' })
          setTimeout(() => this.update({ saveStatus: '' }), 3000)
        }).catch(() => {
          this.update({ saveStatus: 'Saved. Status check failed.' })
        })
      },
    }
  </script>

  <style>
    settings-dialog {
      display: block;
      min-width: min(92vw, 54rem);
    }

    .settings-dialog {
      display: flex;
      flex-direction: column;
      min-height: 0;
    }

    .sm-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--space-md);
    }

    .sm-header h2 {
      margin: 0;
      letter-spacing: 0.12em;
    }

    .sm-tabs {
      display: flex;
      border-bottom: 1px solid color-mix(in srgb, var(--border) 40%, transparent);
      gap: var(--space-md);
      margin-top: var(--space-xs);
      padding-bottom: var(--space-xs);
    }

    .sm-tabs button {
      background: transparent;
      border: none;
      color: var(--text-dim);
      padding: var(--space-sm) var(--space-xs);
      font-size: 0.85rem;
      letter-spacing: 0.05em;
      text-transform: uppercase;
      cursor: pointer;
      position: relative;
      transition: color var(--motion-fast) ease;
    }

    .sm-tabs button:hover { color: var(--text-prose); }
    .sm-tabs button.active { color: var(--gold); font-weight: 600; }
    .sm-tabs button.active::after {
      content: '';
      position: absolute;
      bottom: -0.125rem;
      left: 0;
      right: 0;
      height: 0.125rem;
      background: var(--gold);
    }

    .sm-body {
      overflow: auto;
      min-height: 0;
      padding-right: var(--space-xs);
    }

    .sm-tab-panel {
      padding: var(--space-xs) 0;
    }

    .basic-setup-card {
      background: color-mix(in srgb, var(--surface-card-2) 60%, transparent);
      border: 1px solid color-mix(in srgb, var(--border) 60%, transparent);
      border-radius: var(--space-xs);
      padding: 1.25rem;
      text-align: left;
    }

    .basic-settings-group {
      margin-top: var(--space-md);
      border-top: 1px solid var(--border);
      padding-top: var(--space-md);
    }

    .basic-setup-card h3 { margin: 0 0 var(--space-xs); color: var(--gold); }
    .basic-setup-card p { margin: 0 0 var(--space-md); line-height: 1.5; font-size: var(--space-md); }
    .basic-setup-card ul { margin: 0 0 1.25rem; padding-left: 1.2rem; font-size: 0.9rem; color: var(--text-prose); }
    .basic-setup-card li { margin-bottom: var(--space-xs); }

    .coming-soon-note {
      font-size: 0.85rem !important;
      color: var(--text-dim);
      background: color-mix(in srgb, var(--bg) var(--alpha-high), transparent);
      padding: var(--space-sm);
      border-radius: var(--space-xs);
      border-left: 0.1875rem solid var(--text-dim);
      margin: 0 !important;
    }

    .settings-stack {
      display: flex;
      flex-direction: column;
      gap: var(--space-lg);
    }

    .settings-panel {
      background: color-mix(in srgb, var(--surface-card-1) 42%, transparent);
      border: 1px solid color-mix(in srgb, var(--border) 72%, transparent);
      border-radius: 0.75rem;
      padding: var(--space-md);
      text-align: left;
    }

    .panel-header {
      display: flex;
      flex-direction: column;
      gap: 0.35rem;
      margin-bottom: var(--space-md);
    }

    .panel-header h3 {
      margin: 0;
      color: var(--gold);
      letter-spacing: 0.06em;
      text-transform: uppercase;
      font-size: 0.95rem;
    }

    .panel-header p {
      margin: 0;
      color: var(--text-dim);
      font-size: 0.86rem;
      line-height: 1.45;
    }

    .model-group {
      display: flex;
      flex-direction: column;
      gap: var(--space-sm);
    }

    .model-group + .model-group {
      margin-top: var(--space-md);
      padding-top: var(--space-md);
      border-top: 1px solid color-mix(in srgb, var(--border) 55%, transparent);
    }

    .model-group-header {
      display: flex;
      flex-direction: column;
      gap: 0.2rem;
      margin-bottom: 0.25rem;
    }

    .model-group-header h4 {
      margin: 0;
      color: var(--text-prose);
      font-size: 0.9rem;
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }

    .model-group-header p {
      margin: 0;
      color: var(--text-dim);
      font-size: 0.82rem;
      line-height: 1.4;
    }

    .provider-card {
      background: color-mix(in srgb, var(--surface-card-1) 40%, transparent);
      border: 1px solid var(--border);
      border-radius: 0.5rem;
      padding: var(--space-md);
      margin-bottom: var(--space-md);
      text-align: left;
    }

    .imagen-status-card {
      background: color-mix(in srgb, var(--surface-card-1) 50%, transparent);
      border: 1px solid color-mix(in srgb, var(--border) 60%, transparent);
      border-radius: 0.5rem;
      padding: var(--space-md);
      margin: 0 0 var(--space-md);
      display: flex;
      flex-direction: column;
      gap: var(--space-xs);
    }

    .imagen-status-card .pc-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: var(--space-xs);
    }

    .imagen-status-card .image-status-copy {
      font-size: 0.85rem;
      color: var(--text-muted);
      margin: 0 0 var(--space-sm);
    }

    .imagen-status-card .image-status-note {
      margin: 0;
      font-size: 0.75rem;
      color: var(--text-dim);
    }

    .imagen-status-card .image-status-note a {
      color: var(--gold);
    }

    .pc-header { display: flex; align-items: center; gap: var(--space-xs); margin-bottom: var(--space-xs); }
    .pc-label { font-size: var(--space-md); color: var(--text-prose); }
    .pc-badge { font-size: 0.65rem; padding: 0.1rem var(--space-xs); border-radius: 0.25rem; font-weight: bold; }
    .pc-badge.online { background: rgba(52, 211, 153, 0.2); color: var(--sem-success-fg); }
    .pc-badge.offline { background: rgba(248, 113, 113, 0.2); color: #f87171; }
    .pc-badge.warn { background: rgba(251, 191, 36, 0.2); color: #facc15; }
    .provider-status-dot {
      inline-size: 0.55rem;
      block-size: 0.55rem;
      border-radius: var(--radius-pill);
      background: #f87171;
      box-shadow: 0 0 0.5rem rgba(248, 113, 113, 0.55);
    }
    .provider-status-dot.online {
      background: #34d399;
      box-shadow: 0 0 0.5rem rgba(52, 211, 153, 0.55);
    }

    .pc-desc { font-size: 0.85rem; color: var(--text-muted); margin-bottom: var(--space-xs); line-height: 1.4; }
    .pc-models { font-size: var(--space-sm); color: var(--text-dim); font-family: monospace; margin-bottom: var(--space-sm); }
    .pc-checkbox-label { display: flex; align-items: center; gap: var(--space-xs); font-size: 0.85rem; color: var(--text-prose); margin-bottom: var(--space-sm); cursor: pointer; }
    .pc-key-input { margin-bottom: 0; width: 100%; }
    .pc-hint { font-size: 0.7rem; color: var(--text-dim); text-transform: none; }
    .provider-default-note {
      font-size: 0.75rem;
      color: var(--text-dim);
      margin-top: var(--space-xs);
    }

    .job-section-card {
      background: color-mix(in srgb, var(--surface-card-2) 48%, transparent);
      border: 1px solid color-mix(in srgb, var(--border) 70%, transparent);
      border-radius: 0.65rem;
      padding: var(--space-md);
      margin-bottom: var(--space-md);
    }

    .job-head {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: var(--space-md);
      margin-bottom: var(--space-sm);
    }

    .job-head h4 {
      margin: 0;
      font-size: 0.95rem;
      color: var(--text-prose);
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }

    .job-head p {
      margin: 0.2rem 0 0;
      font-size: 0.85rem;
      color: var(--text-dim);
      line-height: 1.4;
    }

    .job-badge {
      flex: 0 0 auto;
      font-size: 0.7rem;
      color: var(--gold);
      background: color-mix(in srgb, var(--bg) 35%, transparent);
      border: 1px solid color-mix(in srgb, var(--gold) 30%, transparent);
      border-radius: var(--radius-pill);
      padding: 0.25rem 0.55rem;
      margin-top: 0.1rem;
    }

    .recommendation-strip {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(10rem, 1fr));
      gap: var(--space-xs);
      margin-top: var(--space-sm);
    }

    .recommendation-pill {
      border: 1px solid color-mix(in srgb, var(--border) 65%, transparent);
      border-radius: 0.55rem;
      padding: 0.55rem 0.65rem;
      background: color-mix(in srgb, var(--bg) 22%, transparent);
      display: flex;
      flex-direction: column;
      gap: 0.2rem;
      min-height: 4.5rem;
    }

    .recommendation-pill.selected {
      border-color: color-mix(in srgb, var(--gold) 70%, transparent);
      box-shadow: 0 0 0 1px color-mix(in srgb, var(--gold) 25%, transparent);
    }

    .recommendation-pill span {
      font-size: 0.65rem;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: var(--text-dim);
    }

    .recommendation-pill strong {
      font-size: 0.82rem;
      color: var(--text-prose);
      line-height: 1.3;
    }

    .recommendation-pill small {
      font-size: 0.72rem;
      color: var(--text-dim);
      line-height: 1.25;
    }

    .ollama-hint { font-size: var(--space-sm); color: var(--text-dim); font-style: italic; }
    .ollama-loading { display: flex; align-items: center; gap: var(--space-xs); font-size: 0.85rem; color: var(--gold); margin: var(--space-xs) 0; }
    .ollama-quality-group { margin-top: var(--space-xs); }

    .sm-footer {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--space-md);
      margin-top: var(--space-md);
    }

    .sm-status { font-size: var(--space-sm); color: var(--gold); min-height: 1.2rem; }
    .sm-privacy-note { font-size: var(--space-sm); color: var(--text-dim); text-align: center; margin-top: var(--space-lg); }
  </style>
</settings-dialog>
